/**
 * Module dependencies.
 */

import moment from 'moment';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

/**
 * `Props` type.
 */

type Props = {
  date: moment.Moment | string | Date | undefined | null;
  showMilliseconds?: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/**
 * `DateLabel` styled component.
 */

const DateLabel = styled.div`
  background-color: #ddd;
  padding: 2px 6px;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  border: 1px solid #ccc;
  font-size: 13px;
  font-weight: 300;
  height: 25px;
`;

/**
 * `TimeLabel` styled component.
 */

const TimeLabel = styled.div<{
  $last?: boolean;
}>`
  font-size: 14px;
  background-color: #ccc;
  padding: 2px 0 2px 6px;
  height: 25px;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  font-size: 13px;
  font-weight: 500;

  ${ifProp(
    '$last',
    css`
      padding: 2px 6px 2px 0;
      border-top-right-radius: 20px;
      border-bottom-right-radius: 20px;
      font-size: 11px;
      font-weight: 300;
      line-height: 20px;
    `
  )}
`;

/**
 * Export `DateTime` component.
 */

export function DateTime(props: Props): JSX.Element {
  const { showMilliseconds = false } = props;
  const date = useMemo(() => {
    return moment(props.date);
  }, [props.date]);

  return (
    <Wrapper>
      <DateLabel>{date.format('YYYY-MM-DD')}</DateLabel>

      <TimeLabel>{date.format('HH:mm')}</TimeLabel>

      <TimeLabel $last={!showMilliseconds}>{date.format(':ss')}</TimeLabel>

      {showMilliseconds && <TimeLabel $last>{date.format('.SSS')}</TimeLabel>}
    </Wrapper>
  );
}
